import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';

import { Boton } from '#components/mobile/ui/Boton';
import { color, texto } from '#components/mobile/ui/tokens';
import { pedirPuente } from '#uso';

import { Dato, Seccion } from './piezas';
import { fechaCorta, nombreEstadoPlan, nombreTramo, TRAMOS } from './puente';
import type { FilaPersona, Tramo } from './puente';

/**
 * Puerta de planes en la ficha de una persona (solo admin): su estado, su
 * tramo y sus fechas; cambiarle el tramo y regalarle meses.
 */
export function PlanPersona({
  p,
  locale,
  onCambio,
}: {
  p: FilaPersona;
  locale: string;
  onCambio: () => void;
}) {
  const { t } = useTranslation();
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(false);

  async function cambiar(cuerpo: {
    tramo?: Tramo | null;
    regalarMeses?: number;
  }) {
    setGuardando(true);
    setError(false);
    try {
      await pedirPuente('/plan', {
        metodo: 'POST',
        cuerpo: { userId: p.id, ...cuerpo },
      });
      onCambio();
    } catch {
      setError(true);
    } finally {
      setGuardando(false);
    }
  }

  const fecha = (ts: number | null) => fechaCorta(ts, locale) || '—';

  return (
    <Seccion titulo={t('Plan')}>
      <Dato
        titulo={t('Status')}
        valor={nombreEstadoPlan(t, p)}
        tono={p.estadoPlan === 'caducado' ? 'bad' : undefined}
      />
      <Dato titulo={t('Tier')} valor={nombreTramo(t, p.tramo)} />
      {p.gratisHasta != null && (
        <Dato titulo={t('Free until')} valor={fecha(p.gratisHasta)} />
      )}
      {p.pagadoHasta != null && (
        <Dato titulo={t('Paid until')} valor={fecha(p.pagadoHasta)} />
      )}
      {p.borradoEl != null && (
        <Dato
          titulo={t('Account deleted on')}
          valor={fecha(p.borradoEl)}
          tono="bad"
        />
      )}

      <Text style={{ ...texto.secundario, color: color.fg2, marginTop: 4 }}>
        <Trans>Change tier</Trans>
      </Text>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
        {[...TRAMOS, null].map(tramo => (
          <Boton
            key={tramo ?? 'sin'}
            variante={p.tramo === tramo ? 'primario' : 'fantasma'}
            isDisabled={guardando || p.tramo === tramo}
            onPress={() => void cambiar({ tramo })}
          >
            {nombreTramo(t, tramo)}
          </Boton>
        ))}
      </View>

      <Text style={{ ...texto.secundario, color: color.fg2, marginTop: 4 }}>
        <Trans>Give free months</Trans>
      </Text>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
        {[1, 3, 6, 12].map(meses => (
          <Boton
            key={meses}
            variante="fantasma"
            isDisabled={guardando}
            onPress={() => void cambiar({ regalarMeses: meses })}
          >
            {t('+{{count}} months', { count: meses })}
          </Boton>
        ))}
      </View>
      {error && (
        <Text style={{ ...texto.secundario, color: color.bad }}>
          {t('Could not save. Try again.')}
        </Text>
      )}
    </Seccion>
  );
}
